import type {
  ActiveIssuesResponse,
  ActiveIssue,
  IssueDetails,
  PotentialIssuesResponse,
  ResolvedIssuesResponse,
  SupplierIssuesResponse
} from 'components/canary/issue_tracking/issueTrackingTypes'
import { api } from 'components/api'
import {
  canaryActiveIssuesPath,
  canaryDismissIssuePath,
  canaryIssueArchivePath,
  canaryIssuePath,
  canaryIssueResolvePath,
  canaryIssuesPath,
  canaryPotentialIssuesPath,
  canaryResolvedIssuesPath,
  canarySupplierIssuesPath
} from 'components/routes/api'

export const fetchPotentialIssues = async (contractVehicleId: string | number): Promise<PotentialIssuesResponse> => {
  const response = await api.get<unknown, PotentialIssuesResponse>({
    url: canaryPotentialIssuesPath(),
    params: { contract_vehicle_id: contractVehicleId },
    headers: { Accept: 'application/json' },
    cacheOptions: { enabled: false }
  })

  if (response.data.potentialIssues == null) {
    throw new Error('Issue tracking response is missing potentialIssues')
  }

  return response.data
}

export const dismissPotentialIssue = async (reviewId: number): Promise<void> => {
  await api.postWithCsrf({
    url: canaryDismissIssuePath(),
    data: { review_id: reviewId }
  })
}

export const createPotentialIssue = async (reviewId: number): Promise<ActiveIssue> => {
  const response = await api.postWithCsrf<unknown, ActiveIssue>({
    url: canaryIssuesPath(),
    data: { review_id: reviewId }
  })

  return response.data
}

export const resolveIssue = async (issueId: number): Promise<void> => {
  await api.patchWithCsrf({
    url: canaryIssueResolvePath(issueId)
  })
}

export const archiveIssue = async (issueId: number): Promise<void> => {
  await api.patchWithCsrf({
    url: canaryIssueArchivePath(issueId)
  })
}

export const updateIssueNotes = async (issueId: number, notes: string | null): Promise<void> => {
  await api.patchWithCsrf({
    url: canaryIssuePath(issueId),
    data: { notes }
  })
}

export const fetchIssue = async (issueId: number): Promise<IssueDetails> => {
  const response = await api.get<unknown, IssueDetails>({
    url: canaryIssuePath(issueId),
    headers: { Accept: 'application/json' },
    cacheOptions: { enabled: false }
  })

  if (response.data.issueId == null || response.data.review == null) {
    throw new Error('Issue tracking response is missing issue details')
  }

  return response.data
}

export const fetchActiveIssues = async (contractVehicleId: string | number): Promise<ActiveIssuesResponse> => {
  const response = await api.get<unknown, ActiveIssuesResponse>({
    url: canaryActiveIssuesPath(),
    params: { contract_vehicle_id: contractVehicleId },
    headers: { Accept: 'application/json' },
    cacheOptions: { enabled: false }
  })

  if (response.data.activeIssues == null) {
    throw new Error('Issue tracking response is missing activeIssues')
  }

  return response.data
}

export const fetchResolvedIssues = async (contractVehicleId: string | number): Promise<ResolvedIssuesResponse> => {
  const response = await api.get<unknown, ResolvedIssuesResponse>({
    url: canaryResolvedIssuesPath(),
    params: { contract_vehicle_id: contractVehicleId },
    headers: { Accept: 'application/json' },
    cacheOptions: { enabled: false }
  })

  if (response.data.resolvedIssues == null) {
    throw new Error('Issue tracking response is missing resolvedIssues')
  }

  return response.data
}

export const fetchSupplierIssues = async (contractVehicleId: number, supplierSlug: string): Promise<SupplierIssuesResponse> => {
  const response = await api.get<unknown, SupplierIssuesResponse>({
    url: canarySupplierIssuesPath(),
    params: { contract_vehicle_id: contractVehicleId, supplier: supplierSlug },
    headers: { Accept: 'application/json' },
    cacheOptions: { enabled: false }
  })

  if (response.data.activeIssues == null || response.data.resolvedIssues == null) {
    throw new Error('Issue tracking response is missing supplier issues')
  }

  return response.data
}
